import { Canvas, useThree, useFrame, extend } from '@react-three/fiber'
import SceneContents from './components/SceneContents.jsx'
import { Hotspots, HotspotsOverlay, Title, TextBox, LogoGroup, WoomeraBadge, SaxaVordLogo, EnvelopeButton, MuteButton } from './components/UI.jsx'
import { AnimationProvider, useAnimation } from './components/animations.jsx'
import { Suspense, useState, useEffect, useMemo, useRef, useCallback } from 'react'
import * as THREE from 'three/webgpu'
import { EffectComposer, Bloom, Vignette, HueSaturation, Noise, Sepia, ToneMapping } from '@react-three/postprocessing'
import { BlendFunction, ToneMappingMode } from 'postprocessing'
import { Leva, useControls } from 'leva'
import PostProcessingWebGPU from './components/PostProcessingWebGPU.jsx'
import NewsletterPopup from './components/NewsletterPopup.jsx'
import WelcomeBanner from './components/WelcomeBanner.jsx'
import AudioManager from './components/AudioManager.jsx'

// Register the WebGPU-build three namespace so R3F's JSX intrinsics resolve against it.
// Without this, <mesh>/<meshStandardMaterial> resolve against the plain `three` build while
// the renderer is the WebGPU one — objects then draw silently into nothing.
extend(THREE)

// One WebGPURenderer serves every device: it selects a WebGL2 backend automatically when
// navigator.gpu is absent, so the fallback is the same code path that ships.
// ?webgl=true forces the WebGL2 backend (note the =true — a bare ?webgl does nothing).
const FORCE_WEBGL = new URLSearchParams(window.location.search).get('webgl') === 'true'

// Whether the WebGPU backend will actually be selected. Needed at module scope because the
// <Canvas> `orthographic` and `camera` props are evaluated before any renderer exists, and on
// the WebGPU path SceneManager's PerspectiveCamera must be the one and only render camera —
// an orthographic prop camera would otherwise be created and keep reasserting itself.
const WILL_USE_WEBGPU = !FORCE_WEBGL && typeof navigator !== 'undefined' && !!navigator.gpu

// Async gl factory — R3F 9 awaits a promise-returning factory before first render.
const makeRenderer = async (props) => {
  const renderer = new THREE.WebGPURenderer({ ...props, forceWebGL: FORCE_WEBGL })
  await renderer.init()
  // Preserved from the previous inline gl={{...}} object, which the factory replaces.
  // NoToneMapping is deliberate on both paths. WebGL applies tone mapping in the effect
  // composer; WebGPU applies ACES inside renderOutput() in PostProcessingWebGPU. Setting it on
  // the renderer as well would tone-map twice and wash the image out.
  renderer.toneMapping = THREE.NoToneMapping

  // SRGBColorSpace on both paths. three applies outputColorSpace only to the FINAL output
  // target (three.webgpu.js: `isOutputTarget ? outputColorSpace : workingColorSpace`), so a
  // composer's intermediate passes stay in the working space and there is no double encoding.
  renderer.outputColorSpace = THREE.SRGBColorSpace
  // WebGPURenderer builds its depth buffer at the canvas's default 300x150, which mismatches
  // once R3F sizes the canvas — throwing GPUValidationError: depth stencil attachment size
  // does not match. Size it to the real canvas up front; R3F's resize observer keeps it in
  // sync thereafter. The `false` means "don't restyle the element" — R3F owns the CSS size.
  const canvas = props.canvas
  if (canvas) {
    const w = canvas.clientWidth || canvas.width || window.innerWidth
    const h = canvas.clientHeight || canvas.height || window.innerHeight
    renderer.setSize(w, h, false)
  }
  return renderer
}

// Detect if device is mobile or tablet
// Camera rotation is hardcoded to zero — module-level constant avoids object recreation every render
const CAMERA_ROTATION = { rotationZ: 0 }

function isMobileOrTablet() {
  // Check for touch support and screen size
  const isTouchDevice = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0)
  const isSmallScreen = window.innerWidth <= 1024
  
  // Check user agent for mobile/tablet patterns
  const userAgent = navigator.userAgent.toLowerCase()
  const mobileKeywords = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile|tablet/i
  const isMobileUA = mobileKeywords.test(userAgent)
  
  return (isTouchDevice && isSmallScreen) || isMobileUA
}

// Signals to the loading screen that the scene has rendered its first frame
function SceneReadySignal() {
  const hasFired = useRef(false)
  useFrame(() => {
    if (hasFired.current) return

    // On WebGPU the ocean is gated on the shoreline distance field (loaded from public/, or
    // baked at runtime if that asset is absent). Firing on the literal first frame would clear
    // the loading screen before the water and its foam exist, so the user would watch the sea
    // pop in. OceanWaterTSL sets this flag once the water mesh is actually in the scene.
    //
    // The WebGL path has no such dependency, so the flag is pre-set there and this is a no-op.
    if (!window.__oceanReady) return

    hasFired.current = true
    window.__sceneReady = true
  })
  return null
}

// @react-three/postprocessing wraps `postprocessing`, which has zero WebGPU support at its
// latest version (3.0.4): EffectComposer.setRenderer calls getContext().getContextAttributes(),
// a WebGL-only API that throws and takes down the whole Canvas on a WebGPURenderer.
//
// This wrapper gates on the renderer so the inner component — and therefore its useControls
// call — never mounts on WebGPU. Bailing out *inside* the component would still register the
// Leva panel, showing a dev menu full of sliders that do nothing on this renderer.
function PostProcessingLayer() {
  const isWebGPU = useThree(state => !!state.gl?.isWebGPURenderer)
  if (isWebGPU) return null
  return <PostProcessingLayerWebGL />
}

function PostProcessingLayerWebGL() {
  // Leva controls for post-processing (only visible in dev mode)
  const postProcessing = useControls('Post Processing', {
    enabled: { value: true, label: 'Master Enable' },
    
    // Tone Mapping
    toneMappingEnabled: { value: true, label: 'Tone Mapping' },
    toneMappingMode: {
      value: ToneMappingMode.CINEON,
      options: {
        'None': ToneMappingMode.NONE,
        'Reinhard': ToneMappingMode.REINHARD,
        'Reinhard2': ToneMappingMode.REINHARD2,
        'Reinhard2 Adaptive': ToneMappingMode.REINHARD2_ADAPTIVE,
        'Uncharted2': ToneMappingMode.UNCHARTED2,
        'Cineon': ToneMappingMode.CINEON,
        'ACESFilmic': ToneMappingMode.ACES_FILMIC,
      },
      label: 'Mode'
    },
    
    // Bloom
    bloomEnabled: { value: true, label: 'Bloom Enable' },
    bloomIntensity: { value: 0.25, min: 0, max: 3, step: 0.01, label: 'Intensity' },
    bloomThreshold: { value: 0.25, min: 0, max: 1, step: 0.01, label: 'Threshold' },
    bloomSmoothing: { value: 0.58, min: 0, max: 1, step: 0.01, label: 'Smoothing' },
    bloomBlendMode: {
      value: BlendFunction.ADD,
      options: {
        'Add': BlendFunction.ADD,
        'Screen': BlendFunction.SCREEN,
        'Normal': BlendFunction.NORMAL,
        'Overlay': BlendFunction.OVERLAY,
      },
      label: 'Blend Mode'
    },
    
    // Noise
    noiseEnabled: { value: true, label: 'Noise Enable' },
    noiseOpacity: { value: 0.2, min: 0, max: 1, step: 0.01, label: 'Opacity' },
    noisePremultiply: { value: true, label: 'Premultiply' },
    noiseBlendMode: {
      value: BlendFunction.ADD,
      options: {
        'Add': BlendFunction.ADD,
        'Normal': BlendFunction.NORMAL,
        'Multiply': BlendFunction.MULTIPLY,
        'Screen': BlendFunction.SCREEN,
      },
      label: 'Blend Mode'
    },
    
    // Vignette
    vignetteEnabled: { value: false, label: 'Vignette Enable' },
    vignetteDarkness: { value: 0.5, min: 0, max: 1, step: 0.01, label: 'Darkness' },
    vignetteOffset: { value: 0.5, min: 0, max: 1, step: 0.01, label: 'Offset' },
    
    // Hue/Saturation
    hueEnabled: { value: false, label: 'Hue/Sat Enable' },
    hue: { value: 0, min: -Math.PI, max: Math.PI, step: 0.01, label: 'Hue' },
    saturation: { value: 0, min: -1, max: 1, step: 0.01, label: 'Saturation' },
  }, { collapsed: false })
  
  // Only render effects when master switch is enabled
  if (!postProcessing.enabled) {
    return null
  }
  
  return (
    <EffectComposer>
      {postProcessing.toneMappingEnabled && (
        <ToneMapping mode={postProcessing.toneMappingMode} />
      )}
      
      {postProcessing.bloomEnabled && (
        <Bloom
          intensity={postProcessing.bloomIntensity}
          luminanceThreshold={postProcessing.bloomThreshold}
          luminanceSmoothing={postProcessing.bloomSmoothing}
          blendFunction={postProcessing.bloomBlendMode}
        />
      )}

      {postProcessing.noiseEnabled && (
        <Noise
          opacity={postProcessing.noiseOpacity}
          premultiply={postProcessing.noisePremultiply}
          blendFunction={postProcessing.noiseBlendMode}
        />
      )}
      
      {postProcessing.vignetteEnabled && (
        <Vignette
          darkness={postProcessing.vignetteDarkness}
          offset={postProcessing.vignetteOffset}
        />
      )}
      
      {postProcessing.hueEnabled && (
        <HueSaturation
          hue={postProcessing.hue}
          saturation={postProcessing.saturation}
        />
      )}
    </EffectComposer>
  )
}

// Component to read and display the actual renderer DPR
function DPRDisplay({ show }) {
  const [renderDpr, setRenderDpr] = useState(0)
  const [deviceDpr, setDeviceDpr] = useState(window.devicePixelRatio)
  const [screenResolution, setScreenResolution] = useState(`${window.screen.width}x${window.screen.height}`)
  
  useEffect(() => {
    if (!show) return
    
    // Get the canvas element
    const canvas = document.querySelector('canvas')
    if (!canvas) return
    
    // Calculate actual rendering DPR from canvas dimensions
    const updateRenderDpr = () => {
      const rect = canvas.getBoundingClientRect()
      const actualDpr = canvas.width / rect.width
      setRenderDpr(actualDpr)
    }
    
    // Update device DPR (in case window moves between displays)
    const updateDeviceDpr = () => {
      setDeviceDpr(window.devicePixelRatio)
    }
    
    // Update screen resolution
    const updateScreenResolution = () => {
      setScreenResolution(`${window.screen.width}x${window.screen.height}`)
    }
    
    updateRenderDpr()
    updateDeviceDpr()
    updateScreenResolution()
    
    // Update on resize
    window.addEventListener('resize', updateRenderDpr)
    window.addEventListener('resize', updateDeviceDpr)
    window.addEventListener('resize', updateScreenResolution)
    const observer = new ResizeObserver(updateRenderDpr)
    observer.observe(canvas)
    
    return () => {
      window.removeEventListener('resize', updateRenderDpr)
      window.removeEventListener('resize', updateDeviceDpr)
      window.removeEventListener('resize', updateScreenResolution)
      observer.disconnect()
    }
  }, [show])
  
  if (!show) return null
  
  return (
    <div style={{
      position: 'fixed',
      top: '120px',
      left: '10px',
      padding: '8px 12px',
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      color: '#00ff00',
      fontFamily: 'monospace',
      fontSize: '14px',
      borderRadius: '4px',
      zIndex: 9999,
      pointerEvents: 'none',
      lineHeight: '1.5'
    }}>
      <div>Render DPR: {renderDpr.toFixed(2)}</div>
      <div>Device DPR: {deviceDpr.toFixed(2)}</div>
      <div>Resolution: {screenResolution}</div>
    </div>
  )
}

// Black canvas clear colour — RTT path only. On WebGPU <Environment background> sets
// scene.background to the decoded sky texture, and this would overwrite it.
function CanvasBackground() {
  const isWebGPU = useThree(state => !!state.gl?.isWebGPURenderer)
  if (isWebGPU) return null
  return <color attach="background" args={['#000000']} />
}

// Component to update camera zoom dynamically
function CameraZoomUpdater({ zoom }) {
  const { camera } = useThree()

  useEffect(() => {
    // `zoom` here is an ORTHOGRAPHIC zoom (1300–1500), meaningful only for the locked canvas
    // camera on the RTT path. On WebGPU the active camera is the scene's PerspectiveCamera,
    // where a zoom of 1500 collapses the view to nothing. SceneManager drives that camera's
    // FOV from the same value instead.
    if (camera?.isOrthographicCamera) {
      camera.zoom = zoom
      camera.updateProjectionMatrix()
    }
  }, [zoom, camera])

  return null
}

// Component that manages auto-rotation based on scene and orientation
function AppContent({ cameraRotation, children }) {
  const [autoRotation, setAutoRotation] = useState(0)
  const [targetRotation, setTargetRotation] = useState(0)
  const animationStartTimeRef = useRef(null)
  const startRotationRef = useRef(0)
  const animationDurationMs = 3000

  // Check rotation correctness 
  useEffect(() => {
    // Wait for any animations to complete before validating
    const timeoutId = setTimeout(() => {
      // Scene is always upright in single scene mode
      const correctRotation = 0

      // Check current rotation from ref
      const currentRotation = startRotationRef.current
      
      // Only correct if there's a significant difference
      if (Math.abs(currentRotation - correctRotation) > 0.01) {
        setAutoRotation(correctRotation)
        setTargetRotation(correctRotation)
        startRotationRef.current = correctRotation
        animationStartTimeRef.current = null
      }
    }, 200)

    return () => clearTimeout(timeoutId)
  }, [])

  // Camera rotation is fixed to 0 in single scene mode
  // No transitions needed

  // Single unified orientation handler - camera always stays upright in single scene
  useEffect(() => {
    // Camera is always upright in single scene mode
    setTargetRotation(0)
    setAutoRotation(0)
    startRotationRef.current = 0
  }, [])

  // No scene transitions in single scene mode - camera stays upright
  const effectiveRotation = cameraRotation.rotationZ + autoRotation

  // Memoize the children render
  return useMemo(() => children(effectiveRotation), [children, effectiveRotation])
}

export default function App({ loadingComplete }) {
  const [showPerf, setShowPerf] = useState(false)
  const [showHelpers, setShowHelpers] = useState(false)
  const [showControls, setShowControls] = useState(false)
  const [scenesData, setScenesData] = useState(null)
  const [isAtPOI, setIsAtPOI] = useState(false)
  const [activePOI, setActivePOI] = useState(null)
  const [cameraZoom, setCameraZoom] = useState(2500)
  const [viewportInfo, setViewportInfo] = useState({ width: window.innerWidth, height: window.innerHeight })
  const [isTextBoxClosing, setIsTextBoxClosing] = useState(false)
  const [isTextBoxExpanded, setIsTextBoxExpanded] = useState(false)
  const [bannerDismissed, setBannerDismissed] = useState(false)
  const [newsletterVisible, setNewsletterVisible] = useState(false)
  const [muted, setMuted] = useState(false)
  
  // Compute portrait mode
  const isPortrait = window.innerHeight > window.innerWidth
  
  // Add tablet class to body for CSS targeting
  useEffect(() => {
    const updateDeviceClass = () => {
      const isTouchDevice = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0)
      const isTablet = isTouchDevice && window.innerWidth > 767 && window.innerWidth <= 1366
      const isLandscape = window.innerWidth > window.innerHeight
      const isTabletLandscape = isTablet && isLandscape
      
      if (isTabletLandscape) {
        document.body.classList.add('tablet-landscape')
      } else {
        document.body.classList.remove('tablet-landscape')
      }
    }
    
    updateDeviceClass()
    window.addEventListener('resize', updateDeviceClass)
    window.addEventListener('orientationchange', updateDeviceClass)
    
    return () => {
      window.removeEventListener('resize', updateDeviceClass)
      window.removeEventListener('orientationchange', updateDeviceClass)
    }
  }, [])
  
  // Handle POI state changes
  const handlePOIStateChange = useCallback((atPOI, poiName = null) => {
    setIsAtPOI(atPOI)
    setActivePOI(atPOI ? poiName : null)
  }, [])
  
  // Handle text box closing state changes
  const handleTextBoxClosingStateChange = useCallback((isClosing) => {
    setIsTextBoxClosing(isClosing)
  }, [])
  
  // Handle text box expanded state changes
  const handleTextBoxExpandedStateChange = useCallback((isExpanded) => {
    setIsTextBoxExpanded(isExpanded)
  }, [])
  
  // Bridge ref shared between HotspotsOverlay (DOM, outside Canvas) and
  // Hotspots (inside Canvas). HotspotsOverlay writes { dotRefsMap, seedHotspots }
  // into it on mount; Hotspots.useFrame reads it every frame to write positions
  // directly to DOM elements — single-loop, zero stale-frame lag.
  const hotspotsBridgeRef = useRef(null)

  // Store the handleBackToDefault function from HotspotsOverlay
  const backToDefaultHandlerRef = useRef(null)
  
  const handleBackToDefaultReady = useCallback((handler) => {
    backToDefaultHandlerRef.current = handler
  }, [])
  
  const handleBackToDefault = useCallback(() => {
    if (backToDefaultHandlerRef.current) {
      backToDefaultHandlerRef.current()
    }
  }, [])
  
  // Update camera zoom based on device orientation and size
  useEffect(() => {
    const updateCameraZoom = () => {
      const isPortrait = window.innerHeight > window.innerWidth
      const isMobile = window.innerWidth <= 768
      const isTablet = window.innerWidth > 768 && window.innerWidth <= 1024
      const newZoom = isPortrait ? 1300 : 1500
      
      // Update viewport info for debug display
      setViewportInfo({
        width: window.innerWidth,
        height: window.innerHeight,
        isPortrait,
        isMobile,
        isTablet,
        newZoom
      })
      
      setCameraZoom(newZoom)
    }
    
    updateCameraZoom()
    
    // Listen for orientation changes and window resize
    window.addEventListener('orientationchange', updateCameraZoom)
    window.addEventListener('resize', updateCameraZoom)
    
    return () => {
      window.removeEventListener('orientationchange', updateCameraZoom)
      window.removeEventListener('resize', updateCameraZoom)
    }
  }, [])
  
  // Determine DPR based on device type
  const dprSetting = useMemo(() => {
    const isMobile = isMobileOrTablet()
    // Desktop: always 1, Mobile/Tablet: use device DPR clamped to max 1.5 for performance
    return isMobile ? [1, 1.5] : 1
  }, [])

  useEffect(() => {
    // Check for ?dev=true URL parameter on mount
    const urlParams = new URLSearchParams(window.location.search)
    const isDevMode = urlParams.get('dev') === 'true'
    
    
    if (isDevMode) {
      setShowPerf(true)
      setShowHelpers(true)
      setShowControls(true)
    }

    const handleKeyPress = (event) => {
      if (event.key === 'p' || event.key === 'P') {
        setShowPerf(prev => !prev)
        setShowHelpers(prev => !prev)
        setShowControls(prev => !prev)
      }
    }
    
    // Prevent pinch-zoom and double-tap zoom on mobile
    const handleWheel = (event) => {
      if (event.ctrlKey || event.metaKey) {
        event.preventDefault()
      }
    }
    
    const handleTouchMove = (event) => {
      // Prevent pinch-zoom gesture (2+ touch points)
      if (event.touches.length > 1) {
        event.preventDefault()
      }
    }
    
    const handleGestureStart = (event) => {
      event.preventDefault()
    }

    window.addEventListener('keydown', handleKeyPress)
    window.addEventListener('wheel', handleWheel, { passive: false })
    window.addEventListener('touchmove', handleTouchMove, { passive: false })
    window.addEventListener('gesturestart', handleGestureStart, { passive: false })
    
    return () => {
      window.removeEventListener('keydown', handleKeyPress)
      window.removeEventListener('wheel', handleWheel)
      window.removeEventListener('touchmove', handleTouchMove)
      window.removeEventListener('gesturestart', handleGestureStart)
    }
  }, [])

  return (
    <AnimationProvider>
      <AppContent cameraRotation={CAMERA_ROTATION}>
        {(effectiveRotation) => {
          return (
            <>
              <Leva hidden={!showControls} />
              <Canvas
              // "percentage" = PCFShadowMap. Bare `shadows` makes R3F set PCFSoftShadowMap, which
              // three r185 deprecated and silently downgrades to PCFShadowMap anyway — this states
              // it explicitly and drops the console warning. Same shadows as before; visually
              // confirmed by Luke 2026-07-21.
              shadows="percentage"
              // RTT path only: the orthographic camera frames the fullscreen ScreenPlane.
              // On WebGPU the scene is rendered directly by SceneManager's PerspectiveCamera,
              // and any orthographic prop camera here would compete with it.
              orthographic={!WILL_USE_WEBGPU}
              dpr={dprSetting}
              frameloop="always"
              {...(WILL_USE_WEBGPU ? {} : {
                camera: {
                  position: [0, 10, 15],
                  zoom: cameraZoom,
                  near: 0.1,
                  far: 1000
                }
              })}
              gl={makeRenderer}
            >
              {/* Suspense MUST be inside the Canvas. useGLTF/useLoader suspend; with only an
                  outer boundary the suspension unmounts the whole Canvas and tears down the
                  renderer mid-init (measured 2026-07-21: 1 draw call instead of 23). */}
              <Suspense fallback={null}>
                {/* Black backdrop behind the fullscreen ScreenPlane on the RTT path. On WebGPU
                    the scene renders directly and <Environment background> owns the background,
                    so this would paint over the sky. */}
                <CanvasBackground />
                <CameraZoomUpdater zoom={cameraZoom} />
                <SceneContents
                  showHelpers={showHelpers}
                  onScenesReady={setScenesData}
                  isAtPOI={isAtPOI}
                  activePOI={activePOI}
                  cameraZoom={cameraZoom}
                  cameraRotation={effectiveRotation}
                />
                <Hotspots scenesRef={scenesData} cameraRef={scenesData?.camera} hotspotsBridgeRef={hotspotsBridgeRef} />
                {/* Two implementations, one per renderer. PostProcessingLayer (postprocessing
                    package) is WebGL-only and self-gates; PostProcessingWebGPU is the TSL
                    node-pipeline equivalent — bloom + ACES. */}
                <PostProcessingLayer />
                <PostProcessingWebGPU />
                <SceneReadySignal />
              </Suspense>
            </Canvas>
            <DPRDisplay show={showPerf} />
            <TextBox isAtPOI={isAtPOI} activePOI={activePOI} onBackToDefault={handleBackToDefault} onClosingStateChange={handleTextBoxClosingStateChange} onExpandedStateChange={handleTextBoxExpandedStateChange} />
            <HotspotsOverlay isAtPOI={isAtPOI} onPOIStateChange={handlePOIStateChange} scenesRef={scenesData} cameraZoom={cameraZoom} cameraRotation={effectiveRotation} onBackToDefaultReady={handleBackToDefaultReady} hotspotsBridgeRef={hotspotsBridgeRef} />
            <LogoGroup isAtPOI={isAtPOI} isTextBoxClosing={isTextBoxClosing} isTextBoxExpanded={isTextBoxExpanded} />
            <SaxaVordLogo />
            <div className={`right-panel${isAtPOI ? ' poi-active' : ''}${!bannerDismissed ? ' banner-visible' : ''}`}>
              <EnvelopeButton isAtPOI={isAtPOI} bannerDismissed={bannerDismissed} newsletterVisible={newsletterVisible} />
              <MuteButton muted={muted} onToggle={() => setMuted(m => !m)} />
            </div>
            <NewsletterPopup bannerDismissed={bannerDismissed} onVisibleChange={setNewsletterVisible} />
            <WelcomeBanner isAtPOI={isAtPOI} onDismiss={() => setBannerDismissed(true)} loadingComplete={loadingComplete} />
            <AudioManager muted={muted} bannerDismissed={bannerDismissed} />
            <div className="rotate-overlay">
              <span>Please rotate your device</span>
            </div>
          </>
        )}}
      </AppContent>
    </AnimationProvider>
  )
}